import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";

interface CalculationResult {
  regularCost: number;
  shopGlobalCost: number;
  savings: number;
  savingsPercentage: number;
  taxSavings: number;
}

// Calculator component
function SavingsCalculator() {
  const [weight, setWeight] = useState(2.5);
  const [length, setLength] = useState(12);
  const [width, setWidth] = useState(8);
  const [height, setHeight] = useState(4);
  const [declaredValue, setDeclaredValue] = useState(100);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState<CalculationResult | null>(null);

  const calculateSavings = async () => {
    setCalculating(true);

    // Simulate API call to backend calculator
    setTimeout(() => {
      // Sample calculation logic
      const volume = length * width * height;
      const baseShipping = 35 + weight * 8 + volume * 0.01; // Typical international shipping
      const taxSavings = declaredValue * 0.07; // 7% Delaware sales tax savings
      const consolidatedCost = baseShipping * 0.25; // 75% savings through consolidation
      const serviceFee = Math.min(baseShipping * 0.1, 12); // Max $12 service fee
      const totalShopGlobal = consolidatedCost + serviceFee + 5; // +$5 customs processing

      const savings = baseShipping - totalShopGlobal + taxSavings;
      const savingsPercentage = Math.round((savings / baseShipping) * 100);

      setResult({
        regularCost: Math.round(baseShipping),
        shopGlobalCost: Math.round(totalShopGlobal),
        savings: Math.round(savings),
        savingsPercentage,
        taxSavings: Math.round(taxSavings),
      });
      setCalculating(false);
    }, 1000);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
      <h3 className="text-xl font-bold text-center mb-6">
        Calculate Your Savings
      </h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Package Weight (lbs)
          </label>
          <input
            type="number"
            min="0.1"
            step="0.1"
            value={weight}
            onChange={(e) => setWeight(parseFloat(e.target.value) || 0)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="2.5"
          />
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Length (in)
            </label>
            <input
              type="number"
              min="1"
              value={length}
              onChange={(e) => setLength(parseInt(e.target.value) || 0)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Width (in)
            </label>
            <input
              type="number"
              min="1"
              value={width}
              onChange={(e) => setWidth(parseInt(e.target.value) || 0)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Height (in)
            </label>
            <input
              type="number"
              min="1"
              value={height}
              onChange={(e) => setHeight(parseInt(e.target.value) || 0)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Declared Value (CAD)
          </label>
          <input
            type="number"
            min="1"
            value={declaredValue}
            onChange={(e) => setDeclaredValue(parseInt(e.target.value) || 0)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="100"
          />
        </div>

        <button
          onClick={calculateSavings}
          disabled={calculating}
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
          {calculating ? "Calculating..." : "Calculate Savings"}
        </button>
      </div>

      {result && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-800 mb-3">
            🎉 Your Savings Results
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Regular Shipping:</span>
              <span className="line-through text-gray-500">
                ${result.regularCost}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">ShopGlobal Pro:</span>
              <span className="font-semibold text-green-700">
                ${result.shopGlobalCost}
              </span>
            </div>
            <div className="border-t border-green-300 pt-2">
              <div className="flex justify-between font-semibold text-lg">
                <span className="text-green-800">You Save:</span>
                <span className="text-green-600">
                  ${result.savings} ({result.savingsPercentage}%)
                </span>
              </div>
              <p className="text-xs text-green-600 mt-1">
                Includes ${result.taxSavings} tax savings from Delaware address
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 text-center">
        <button
          onClick={() => (window.location.href = "/signup")}
          className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700">
          Get Started - Save ${result ? result.savings : "Money"}
        </button>
      </div>
    </div>
  );
}

function LandingPage() {
  const [activeSection, setActiveSection] = useState("hero");
  const [showCalculator, setShowCalculator] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">SG</span>
              </div>
              <span className="ml-3 text-xl font-bold text-gray-900">
                ShopGlobal Pro
              </span>
            </div>
            <nav className="hidden md:flex items-center space-x-6">
              <button
                onClick={() => setActiveSection("how-it-works")}
                className="text-gray-600 hover:text-blue-600 font-medium">
                How It Works
              </button>
              <button
                onClick={() => setActiveSection("pricing")}
                className="text-gray-600 hover:text-blue-600 font-medium">
                Pricing
              </button>
              <button
                onClick={() => setShowCalculator(true)}
                className="text-gray-600 hover:text-blue-600 font-medium">
                Calculator
              </button>
            </nav>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => (window.location.href = "/login")}
                className="text-blue-600 hover:text-blue-700 font-medium">
                Sign In
              </button>
              <button
                onClick={() => (window.location.href = "/signup")}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700">
                Get Started Free
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Enhanced */}
      <section id="hero" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Shop US Stores.
                <br />
                <span className="text-blue-600">Ship to Canada Smartly.</span>
              </h1>
              <p className="text-lg lg:text-xl text-gray-600 mb-8 max-w-2xl">
                Save 40-80% on every international shipment with your own
                Delaware tax-free address and AI-powered consolidation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                <button
                  onClick={() => (window.location.href = "/signup")}
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 shadow-lg">
                  Start Saving Money Now
                </button>
                <button
                  onClick={() => setShowCalculator(true)}
                  className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50">
                  Calculate Your Savings
                </button>
              </div>
              <p className="text-sm text-gray-500">
                ✨ Free trial • 🎯 Save up to 80% • 📞 Canadian support
              </p>
            </div>

            {/* Savings Preview */}
            <div className="bg-white rounded-lg shadow-xl p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Real Customer Savings
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-3xl font-bold text-green-600 mb-1">
                    $127
                  </div>
                  <div className="text-sm text-green-700">
                    Saved on iPhone 15
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-3xl font-bold text-blue-600 mb-1">
                    $894
                  </div>
                  <div className="text-sm text-blue-700">
                    Total annual savings
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-3xl font-bold text-purple-600 mb-1">
                    67%
                  </div>
                  <div className="text-sm text-purple-700">Average savings</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="text-3xl font-bold text-orange-600 mb-1">
                    2hrs
                  </div>
                  <div className="text-sm text-orange-700">Setup time</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Stats */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">
                57,349
              </div>
              <div className="text-gray-600">Packages Shipped</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-green-600 mb-2">
                $3.2M
              </div>
              <div className="text-gray-600">Total Savings</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">
                7,842
              </div>
              <div className="text-gray-600">Happy Canadians</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-600 mb-2">
                4.9/5
              </div>
              <div className="text-gray-600">Customer Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">
            How It Works - Simple!
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                1️⃣
              </div>
              <h3 className="font-semibold mb-2">Get Your Address</h3>
              <p className="text-gray-600">
                Sign up in 2 minutes and get your Delaware address. Tax-free
                shipping from day one.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                🛒
              </div>
              <h3 className="font-semibold mb-2">Shop & Ship</h3>
              <p className="text-gray-600">
                Shop any US store and use your Delaware address. We handle
                customs and consolidation automatically.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                🚚
              </div>
              <h3 className="font-semibold mb-2">We Consolidate</h3>
              <p className="text-gray-600">
                Multiple packages become one shipment. Pay once for shipping,
                not 3-5 times. Massive savings.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                📦
              </div>
              <h3 className="font-semibold mb-2">Receive in Canada</h3>
              <p className="text-gray-600">
                Packages arrive at your Canadian address. Track everything in
                real-time from your phone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">
            Why ShopGlobal Pro Saves You Money
          </h2>
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Tax Savings */}
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl">
                🏪
              </div>
              <h3 className="text-xl font-bold mb-4">
                Tax-Free Delaware Address
              </h3>
              <p className="text-gray-600 mb-6">
                Save ~7% on every purchase. Delaware has no sales tax, so you
                save on items that would normally be taxed in your province.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">$200 iPhone case</p>
                <p className="font-semibold text-green-600">
                  You save: $14 tax
                </p>
              </div>
            </div>

            {/* Consolidation */}
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl">
                📦📦
              </div>
              <h3 className="text-xl font-bold mb-4">
                Smart Package Consolidation
              </h3>
              <p className="text-gray-600 mb-6">
                Combine 3-5 packages into one shipment. Pay one shipping fee
                instead of four. Average 60-80% shipping savings.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">
                  3 packages separately: $75
                </p>
                <p className="font-semibold text-green-600">
                  Consolidated: $15 (80% savings)
                </p>
              </div>
            </div>

            {/* No Hassles */}
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl">
                📱
              </div>
              <h3 className="text-xl font-bold mb-4">Zero Hassle Management</h3>
              <p className="text-gray-600 mb-6">
                One dashboard, one payment, one delivery. Real-time tracking,
                insurance options, and 24/7 Canadian support.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">
                  Manage everything easily
                </p>
                <p className="font-semibold text-purple-600">
                  From your phone or computer
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transparent Pricing */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8">
            Simple, Transparent Pricing
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            No hidden fees. Know exactly what you'll pay. Cancel anytime.
          </p>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-blue-600 text-white text-center py-6">
                <h3 className="text-2xl font-bold mb-2">Pay Per Package</h3>
                <p className="text-sm opacity-90">
                  Only when you ship, never monthly fees
                </p>
              </div>

              <div className="p-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold mb-4">Shipping Costs</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Flat shipping rate:</span>
                        <span>$8-25 (based on weight/size)</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Consolidation savings:</span>
                        <span className="text-green-600">
                          $10-50+ per group
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>International handling:</span>
                        <span>$5 flat fee</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-4">Additional Services</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Insurance (optional):</span>
                        <span>$2-8 (1% of value)</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Storage (per day):</span>
                        <span>$1 after 45 days free</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Repackaging:</span>
                        <span>Free</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6 mt-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-semibold text-green-600">
                        Typical Shipping Example:
                      </h4>
                      <p className="text-sm text-gray-600">
                        $200 headset (normally $67 shipping + $14 tax = $281
                        total)
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-600">
                        $34
                      </div>
                      <div className="text-sm text-gray-600">
                        $19 shipping + $15 tax saved
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mt-6">
                  <div className="flex items-center justify-center text-green-600 text-sm font-medium">
                    🔒 No hidden fees • 🎯 Money-back guarantee • 📞 Free
                    Canadian support
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">
            Real Canadians, Real Savings
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  SJ
                </div>
                <div className="ml-3">
                  <div className="font-semibold">Sarah Johnson</div>
                  <div className="text-sm text-gray-600">Vancouver, BC</div>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                "Saved $247 shipping 3 iPhone accessories. No hassle
                consolidation was amazing!"
              </p>
              <div className="flex text-yellow-400">
                ⭐⭐⭐⭐⭐{" "}
                <span className="text-gray-500 text-sm ml-2">6 months ago</span>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  MR
                </div>
                <div className="ml-3">
                  <div className="font-semibold">Mike Robertson</div>
                  <div className="text-sm text-gray-600">Toronto, ON</div>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                "Consolidated 5 Amazon packages into one $12 shipment instead of
                $98. Incredible!"
              </p>
              <div className="flex text-yellow-400">
                ⭐⭐⭐⭐⭐{" "}
                <span className="text-gray-500 text-sm ml-2">3 months ago</span>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  AC
                </div>
                <div className="ml-3">
                  <div className="font-semibold">Angela Chen</div>
                  <div className="text-sm text-gray-600">Montreal, QC</div>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                "So easy! Got my tax-free Delaware address in 2 minutes. Saving
                hundreds monthly."
              </p>
              <div className="flex text-yellow-400">
                ⭐⭐⭐⭐⭐{" "}
                <span className="text-gray-500 text-sm ml-2">8 months ago</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-700 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Stop Paying 3x More to Ship.
            <br />
            Save Big Today.
          </h2>
          <p className="text-lg lg:text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Join 7,842 Canadians already saving thousands. Get your Delaware
            address and start shipping smarter.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <button
              onClick={() => (window.location.href = "/signup")}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 shadow-lg">
              Get Started Free - Save Money Now
            </button>
            <button
              onClick={() => setShowCalculator(true)}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600">
              Calculate My Exact Savings
            </button>
          </div>
          <p className="text-sm opacity-75">
            ✦ Free 45-day storage ✦ No setup fees ✦ Cancel anytime ✦ 24/7
            Canadian support ✦
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">
            Frequently Asked Questions
          </h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-lg mb-3">
                How much money will I actually save?
              </h3>
              <p className="text-gray-600">
                Average customers save $200-500 monthly. Tax savings +
                consolidation = big wins. For example, shipping 3 packages
                separately costs ~$75. We consolidate into 1 shipment for $15.
                That's $60 saved + tax benefits.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-lg mb-3">
                What's the real cost to use ShopGlobal Pro?
              </h3>
              <p className="text-gray-600">
                No monthly fees. You only pay when shipping: $8-25 shipping + $5
                international handling. Optional insurance ($2-8) and storage
                ($1/day after 45 free days). Completely transparent - no hidden
                surprises.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-lg mb-3">
                How long does delivery take?
              </h3>
              <p className="text-gray-600">
                7-12 days total (2-4 days to Delaware + 5-8 days to Canada).
                Faster than ordering direct from international sellers. All
                tracking included.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="font-bold text-sm">SG</span>
                </div>
                <span className="ml-2 font-bold text-lg">ShopGlobal Pro</span>
              </div>
              <p className="text-gray-400 text-sm">
                Saving Canadians thousands on international shipping since 2024.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Products</h4>
              <div className="space-y-2 text-sm">
                <div>
                  <a href="#" className="text-gray-400 hover:text-white">
                    How It Works
                  </a>
                </div>
                <div>
                  <a href="#" className="text-gray-400 hover:text-white">
                    Pricing
                  </a>
                </div>
                <div>
                  <a href="#" className="text-gray-400 hover:text-white">
                    Calculator
                  </a>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <div className="space-y-2 text-sm">
                <div>
                  <a href="#" className="text-gray-400 hover:text-white">
                    Help Center
                  </a>
                </div>
                <div>
                  <a href="#" className="text-gray-400 hover:text-white">
                    Contact
                  </a>
                </div>
                <div>
                  <a href="#" className="text-gray-400 hover:text-white">
                    Status
                  </a>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <div className="space-y-2 text-sm">
                <div>
                  <a href="#" className="text-gray-400 hover:text-white">
                    About
                  </a>
                </div>
                <div>
                  <a href="#" className="text-gray-400 hover:text-white">
                    Careers
                  </a>
                </div>
                <div>
                  <a href="#" className="text-gray-400 hover:text-white">
                    Blog
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2025 ShopGlobal Pro. All rights reserved. 🇨🇦 Made in Canada.
            </p>
            <div className="flex items-center space-x-4 text-sm text-gray-400 mt-4 md:mt-0">
              <span>🔒 SSL Secured</span>
              <span>💳 Stripe Payments</span>
              <span>📞 1-800-SHOP-GLO</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Calculator Modal */}
      {showCalculator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="relative">
            <button
              onClick={() => setShowCalculator(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl">
              ✕
            </button>
            <SavingsCalculator />
          </div>
        </div>
      )}
    </div>
  );
}

export default LandingPage;
